import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PatientList from './PatientList'
import Edit from './Edit'
import GroupsMenu from './GroupsMenu'
import MessagingButtons from './MessagingButtons'
import {
  fetchPatientsThenSave,
  changeView,
  updatePatientSearch,
  updatePatientsAllLocations,
  togglePatientStatusFilter,
  toggleApptTypeFilter,
} from '../../state/actions/patient-groups'
import { fetchApptTypes } from '../../state/actions/appointment-types'
import { prepareBlastMessage } from '../../state/actions/blast-messages'

import css from './styles.css'

class PatientGroups extends Component {
  static propTypes = {
    activeLocation: PropTypes.shape({
      active: PropTypes.bool.isRequired,
      additional_recipients: PropTypes.arrayOf(PropTypes.object),
      city: PropTypes.string,
      country_code: PropTypes.string,
      created_at: PropTypes.string,
      doctor_id: PropTypes.string,
      earliest_same_day_reminder_hour: PropTypes.number,
      facebook_url: PropTypes.string,
      forwarding_email: PropTypes.string,
      google_url: PropTypes.string,
      id: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      name: PropTypes.string,
      office_email: PropTypes.string,
      office_phone: PropTypes.string,
      phone_numbers: PropTypes.arrayOf(PropTypes.object),
      practice_id: PropTypes.number,
      state: PropTypes.string,
      street_address: PropTypes.string,
      timezone: PropTypes.string,
      updated_at: PropTypes.string,
      website: PropTypes.string,
      yelp_url: PropTypes.string,
      zip: PropTypes.string,
    }),
    allLocations: PropTypes.bool.isRequired,
    currentPage: PropTypes.number.isRequired,
    doctors: PropTypes.arrayOf(PropTypes.object).isRequired,
    fetchPatientsThenSave: PropTypes.func.isRequired,
    filters: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        param: PropTypes.string,
        value: PropTypes.func,
      }),
    ).isRequired,
    history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
    isEditing: PropTypes.bool.isRequired,
    isFetching: PropTypes.bool.isRequired,
    loadApptTypes: PropTypes.func.isRequired,
    locations: PropTypes.arrayOf(PropTypes.object).isRequired,
    onClickEdit: PropTypes.func.isRequired,
    patients: PropTypes.arrayOf(PropTypes.object).isRequired,
    patientStatuses: PropTypes.arrayOf(PropTypes.object).isRequired,
    prepareBlastMessage: PropTypes.func.isRequired,
    remainingBlastMessages: PropTypes.number.isRequired,
    search: PropTypes.string.isRequired,
    searchPatients: PropTypes.func.isRequired,
    selectedMenu: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      type: PropTypes.string.isRequired,
    }).isRequired,
    tier: PropTypes.number,
    togglePatientStatusFilter: PropTypes.func.isRequired,
    toggleApptTypeFilter: PropTypes.func.isRequired,
    totalPages: PropTypes.number,
    totalPatients: PropTypes.number,
    updateAllLocations: PropTypes.func.isRequired,
    userCountryCode: PropTypes.string.isRequired,
  }

  state = {
    isPreparingBlast: false,
    isFetching: false,
    sortCol: 'last_name', // ['first_name', 'last_name', 'email', 'phone']
    sortDir: 'asc',
    selectedDoctorId: '',
    selectedLocationId: '',
    selectedAppointmentTypeIds: [],
    selectedPatientStatusIds: [],
    fetchController: null,
  }

  componentDidMount() {
    const { loadApptTypes, toggleApptTypeFilter, togglePatientStatusFilter } =
      this.props
    this.handleFetchNewPatientList()
    loadApptTypes()
    togglePatientStatusFilter(false)
    toggleApptTypeFilter(false)
  }

  componentDidUpdate({
    selectedMenu: { id: prevMenuId },
    search: prevSearch,
    activeLocation: prevActiveLocation,
    allLocations: prevAllLocations,
  }) {
    const {
      allLocations,
      selectedMenu,
      search,
      activeLocation,
      updateAllLocations,
    } = this.props
    if (
      selectedMenu.id !== prevMenuId ||
      search !== prevSearch ||
      (activeLocation && activeLocation.id !== prevActiveLocation.id) ||
      allLocations !== prevAllLocations
    ) {
      if (
        activeLocation &&
        activeLocation.id !== prevActiveLocation.id &&
        allLocations
      ) {
        // ESLint doesn't want me to use setState here... but we want to set allLocations to false
        // if the user changes the activeLocation
        updateAllLocations(false)
        this.handleFetchNewPatientList()
      } else {
        this.handleFetchNewPatientList()
      }
    }
    if (selectedMenu.id !== prevMenuId && selectedMenu.id === 'all') {
      this.handleClearAllFilters()
    }
  }

  handleClearFilter = type => {
    const { toggleApptTypeFilter, togglePatientStatusFilter } = this.props
    const clearedState = typeof this.state[type] === 'string' ? '' : []
    this.setState({ [type]: clearedState }, this.handleFetchNewPatientList)
    if (type === 'selectedPatientStatusIds') togglePatientStatusFilter(false)
    if (type === 'selectedAppointmentTypeIds') toggleApptTypeFilter(false)
  }

  handleClearAllFilters = () => {
    const {
      updateAllLocations,
      toggleApptTypeFilter,
      togglePatientStatusFilter,
    } = this.props
    updateAllLocations(false)
    togglePatientStatusFilter(false)
    toggleApptTypeFilter(false)
    this.setState(
      {
        selectedDoctorId: '',
        selectedLocationId: '',
        selectedAppointmentTypeIds: [],
        selectedPatientStatusIds: [],
      },
      this.handleFetchNewPatientList,
    )
  }

  newPatientQueryParams = () => {
    const {
      selectedAppointmentTypeIds,
      selectedDoctorId,
      selectedLocationId,
      selectedPatientStatusIds,
      sortCol,
      sortDir,
    } = this.state

    const {
      params: { param, value },
      search,
    } = this.props

    const queryParams = {
      search,
      sort_col: sortCol,
      sort_dir: sortDir,
      with_reminders_contact_information: true,
      'appointment_doctor_ids[]': selectedDoctorId || [],
      'appointment_location_ids[]': selectedLocationId || [],
      'appointment_type_ids[]': selectedAppointmentTypeIds || [],
      'patient_status_ids[]': selectedPatientStatusIds || [],
    }

    if (param && value) {
      queryParams[param] = value()
    }
    return queryParams
  }

  handleSearchChange = e => this.props.searchPatients(e.target.value)

  fetchNextPage = () => {
    const {
      activeLocation,
      allLocations,
      currentPage,
      fetchPatientsThenSave,
      hasDSO,
      totalPages,
      practiceName
    } = this.props
    const nextPage = currentPage + 1
    const locationId = hasDSO && activeLocation && activeLocation.id
    if (nextPage <= totalPages || practiceName === 'Specialty Smile Partners, LLC') {
      fetchPatientsThenSave(
        this.newPatientQueryParams(),
        nextPage,
        locationId,
        allLocations,
      )
    }
  }

  fetchNewPatientList = () => {
    const {
      activeLocation,
      allLocations,
      fetchPatientsThenSave,
      hasDSO,
    } =
      this.props
    const locationId = hasDSO && activeLocation && activeLocation.id

    const fetchController = new AbortController()

    this.setState({ isFetching: true, fetchController }, () => {
      fetchPatientsThenSave(
        this.newPatientQueryParams(),
        1,
        locationId,
        allLocations,
        fetchController,
      )
      .then(() => {
        this.setState({ isFetching: false })
      });
    });
  }

  handleFetchNewPatientList = () => {
    if (this.state.fetchController) {
      this.state.fetchController.abort()
      setTimeout(() => {
        this.fetchNewPatientList()
      }, 100)
    } else {
      this.fetchNewPatientList()
    }
  }

  toggleAllLocations = e => this.props.updateAllLocations(e.target.checked)

  handleSortThenFetchPatients = e => {
    const nextCol = e.currentTarget.dataset.column
    const nextDir =
      nextCol !== this.state.sortCol || this.state.sortDir === 'desc'
        ? 'asc'
        : 'desc'

    this.setState(
      { sortCol: nextCol, sortDir: nextDir },
      this.handleFetchNewPatientList,
    )
  }

  selectAppointmentDoctor = e =>
    this.setState(
      { selectedDoctorId: e.target.value },
      this.handleFetchNewPatientList,
    )

  selectAppointmentLocation = e =>
    this.setState(
      { selectedLocationId: e.target.value },
      this.handleFetchNewPatientList,
    )

  selectATypesAndPStatuses = (types, statuses) => {
    this.props.togglePatientStatusFilter(!!statuses && statuses.length > 0)
    this.props.toggleApptTypeFilter(!!types && types.length > 0)
    this.setState(
      { selectedAppointmentTypeIds: types, selectedPatientStatusIds: statuses },
      this.handleFetchNewPatientList,
    )
  }

  prepareBlastMessage = ({ isEmail }) => {
    const {
      activeLocation,
      allLocations,
      history,
      prepareBlastMessage,
    } = this.props

    this.setState({ isPreparingBlast: true })
    const locationId = activeLocation && activeLocation.id

    prepareBlastMessage(
      this.newPatientQueryParams(),
      isEmail,
      locationId,
      allLocations,
    ).then(() => {
      history.push('/messages')
    })
  }

  prepareBlastText = () => {
    this.prepareBlastMessage({ isEmail: false })
  }

  prepareBlastEmail = () => {
    this.prepareBlastMessage({ isEmail: true })
  }

  render() {
    const isTops = !!process.env.REACT_APP_IS_TOPS
    const {
      allLocations,
      currentPage,
      doctors,
      filters,
      hasDSO,
      isEditing,
      isFetching,
      locations,
      onClickEdit,
      patients,
      patientStatuses,
      remainingBlastMessages,
      search,
      selectedMenu,
      tier,
      totalPages,
      totalPatients,
      userCountryCode,
    } = this.props

    const patientsWithApptsInTimeRange =
      selectedMenu.id !== 'all' && selectedMenu.type === 'filters'

    const filterTimeRange = filters.find(
      filter => selectedMenu.id === filter.id,
    ).name

    return (
      <div className={css.container}>
        <div className={css.main}>
          {isEditing ? (
            <Edit />
          ) : (
            <PatientList
              {...this.state}
              allLocationsChecked={allLocations}
              currentPage={currentPage}
              handleFetchNewPatientList={this.handleFetchNewPatientList}
              fetchNextPage={this.fetchNextPage}
              filterTimeRange={filterTimeRange}
              handleAddType={this.handleAddType}
              handleClearFilter={this.handleClearFilter}
              handleRemoveType={this.handleRemoveType}
              handleSearchChange={this.handleSearchChange}
              handleSortThenFetchPatients={this.handleSortThenFetchPatients}
              hasDSO={hasDSO}
              isFetching={isFetching}
              isTops={isTops}
              patientsWithApptsInTimeRange={patientsWithApptsInTimeRange}
              key="list"
              onClickEdit={onClickEdit}
              patients={patients}
              patientStatuses={patientStatuses}
              prepareBlastText={this.prepareBlastText}
              prepareBlastEmail={this.prepareBlastEmail}
              search={search}
              selectAppointmentDoctor={this.selectAppointmentDoctor}
              selectAppointmentLocation={this.selectAppointmentLocation}
              selectATypesAndPStatuses={this.selectATypesAndPStatuses}
              selectedMenu={selectedMenu}
              totalPages={totalPages}
              totalPatients={totalPatients}
              toggleAllLocations={this.toggleAllLocations}
              userCountryCode={userCountryCode}
              doctors={doctors}
              locations={locations}
            />
          )}
        </div>

        <div className={css.menu}>
          <GroupsMenu />
          {(patientsWithApptsInTimeRange || selectedMenu.id === 'all') && (
            <MessagingButtons
              onClickEmail={this.prepareBlastEmail}
              onClickText={this.prepareBlastText}
              tier={tier}
              selectedMenuId={selectedMenu.id}
              remainingBlastMessages={remainingBlastMessages}
            />
          )}
        </div>
      </div>
    )
  }
}

const mapStateToProps = ({
  patientGroups,
  patientGroups: {
    allLocations,
    currentPage,
    doctors,
    filters,
    isFetching,
    locations,
    patients,
    patientStatuses = [],
    search,
    selectedMenu,
    selectedMenu: { id, type },
    totalPatients,
    totalPages,
    view,
  },
  account: {
    auth: { tier, remainingBlastMessages, hasDSO },
    user: { activeLocation, countryCode, practiceName },
  },
}) => ({
  allLocations,
  activeLocation,
  currentPage,
  doctors,
  filters,
  hasDSO,
  isEditing: view === 'edit',
  isFetching,
  locations,
  params: patientGroups[type].find(({ id: indexId }) => id === indexId),
  patients,
  patientStatuses,
  remainingBlastMessages,
  search,
  selectedMenu,
  totalPages,
  tier,
  totalPatients,
  userCountryCode: countryCode,
  practiceName
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      fetchPatientsThenSave,
      loadApptTypes: () => fetchApptTypes(true),
      onClickEdit: () => changeView('edit'),
      prepareBlastMessage,
      searchPatients: number => updatePatientSearch(number),
      updateAllLocations: checked => updatePatientsAllLocations(checked),
      toggleApptTypeFilter,
      togglePatientStatusFilter,
    },
    dispatch,
  )

export default connect(mapStateToProps, mapDispatchToProps)(PatientGroups)



// WEBPACK FOOTER //
// ./src/components/PatientGroups/index.js